import React from "react";
import { Button } from "antd";
import { PlusOutlined, FolderAddOutlined, StopFilled } from "@ant-design/icons";
import styles from "./ProjectFolderEmptyState.module.scss";

const BG_INTERNAL = `${process.env.PUBLIC_URL || ""}/images/LineBGInternal.png`;
const BG_EXTERNAL = `${process.env.PUBLIC_URL || ""}/images/LineBGExt.png`;
const ICON_INTERNAL = `${process.env.PUBLIC_URL || ""}/images/NoDataGraphicInt.svg`;
const ICON_EXTERNAL = `${process.env.PUBLIC_URL || ""}/images/NoDataGraphic.svg`;

export type ProjectFolderEmptyVariant = "internal" | "external";

export interface ProjectFolderEmptyStateProps {
  variant: ProjectFolderEmptyVariant;
  title: string;
  /** İkincil açıklama; boş veya yoksa paragraf gösterilmez */
  hint?: string;
  /** `messageOnly` dışındaki boş klasör modunda gerekir */
  uploadLabel?: string;
  createFolderLabel?: string;
  onUploadFile?: () => void;
  onCreateFolder?: () => void;
  /** Upload butonu (dosya seçici) */
  showUpload?: boolean;
  /** Klasör oluştur (yetkiye bağlı) */
  showCreateFolder?: boolean;
  /**
   * Sürükle-bırak veya dosya seçici açıkken: tüm alan (metin, ikon, butonlar) opacity 0.5,
   * LineBG kapalı.
   */
  interactionDimmed?: boolean;
  /**
   * Boş klasör aksiyonları yok: illüstrasyon + başlık + isteğe bağlı açıklama + tek buton (403, not found).
   * `variant` ile internal / external ayrımı aynen uygulanır.
   */
  messageOnly?: boolean;
  /**
   * messageOnly iken: true ise boş sayfa SVG’si (NoDataGraphic), false varsayılan StopFilled.
   */
  useEmptyIllustration?: boolean;
  actionLabel?: string;
  onAction?: () => void;
  /**
   * Arka plan (LineBG), kesik border ve tam yükseklik yok; içerik üstten hizalanır (ör. projeler listesi boş).
   */
  plain?: boolean;
}

/**
 * Boş klasör / proje: LineBG + basit kesik çerçeve; interactionDimmed ile tam alan soluk, arka plan deseni kapalı.
 */
const ProjectFolderEmptyState: React.FC<ProjectFolderEmptyStateProps> = ({
  variant,
  title,
  hint = '',
  uploadLabel = '',
  createFolderLabel = '',
  onUploadFile,
  onCreateFolder,
  showUpload = true,
  showCreateFolder = true,
  interactionDimmed = false,
  messageOnly = false,
  useEmptyIllustration = false,
  actionLabel,
  onAction,
  plain = false,
}) => {
  const bgUrl = variant === "external" ? BG_EXTERNAL : BG_INTERNAL;
  const iconSrc = variant === "external" ? ICON_EXTERNAL : ICON_INTERNAL;
  const showMessageAction = messageOnly && actionLabel && onAction;
  const showActions =
    !messageOnly && (showUpload || showCreateFolder);
  const variantClass =
    variant === "external" ? styles.external : styles.internal;
  const showBg = !plain && !interactionDimmed;

  return (
    <div
      className={`${styles.root} ${variantClass} ${interactionDimmed ? styles.dimmed : ""} ${plain ? styles.plain : ""}`}
      data-empty-state
    >
      {showBg ? (
        <div
          className={styles.bg}
          style={{ backgroundImage: `url(${bgUrl})` }}
          aria-hidden
        />
      ) : null}
      <div className={styles.body}>
        {messageOnly ? (
          useEmptyIllustration ? (
            <img src={iconSrc} alt="" className={styles.icon} />
          ) : (
            <div className={styles.messageIcon} aria-hidden>
              <StopFilled />
            </div>
          )
        ) : (
          <img src={iconSrc} alt="" className={styles.icon} />
        )}
        <h2 className={styles.title}>{title}</h2>
        {hint?.trim() ? <p className={styles.hint}>{hint}</p> : null}
        {showMessageAction ? (
          <div className={styles.actions}>
            <Button
              type="default"
              className={styles.btnSecondary}
              onClick={() => onAction?.()}
            >
              {actionLabel}
            </Button>
          </div>
        ) : null}
        {showActions ? (
          <div className={styles.actions}>
            {showUpload ? (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className={styles.btnPrimary}
                onClick={() => onUploadFile?.()}
              >
                {uploadLabel}
              </Button>
            ) : null}
            {showCreateFolder ? (
              <Button
                icon={<FolderAddOutlined />}
                className={styles.btnSecondary}
                onClick={() => onCreateFolder?.()}
              >
                {createFolderLabel}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ProjectFolderEmptyState;
